import React, { useState, useEffect } from 'react';
import { walletService } from '../../services/WalletService';
import RedemptionModal from './RedemptionModal';
import { Wallet, IndianRupee, ArrowUpRight, ArrowDownRight, Clock, CheckCircle2, XCircle, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

const WalletPage = () => {
    const [walletData, setWalletData] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
    
    const location = useLocation();
    
    // Determine UserType from URL (e.g. /activity/wallet -> Activity)
    const userType = location.pathname.includes('/activity') ? 'Activity' 
                   : location.pathname.includes('/restaurant') ? 'Restaurant' 
                   : 'Shop';
                   
    // Get Partner ID securely from Token
    const tokenKey = `token_partner_${userType.toLowerCase() === 'activity' ? 'activities' : userType.toLowerCase()}`;
    const token = localStorage.getItem(tokenKey);
    const partnerId = token ? JSON.parse(atob(token.split('.')[1]))?.business_id : null;

    const fetchWalletDetails = async (isSilent = false) => {
        try {
            if (!isSilent) setIsLoading(true);
            const data = await walletService.getWalletDetails(userType, partnerId);
            setWalletData({ balance: data.balance || 0 });
            setTransactions(data.transactions || []);
        } catch (error) {
            console.error('Error fetching wallet:', error);
            if (!isSilent) toast.error('Failed to load wallet details');
        } finally {
            setIsLoading(false);
        }
    };

    const downloadCSV = () => {
        if (!transactions || transactions.length === 0) {
            toast.error("No transactions to download");
            return;
        }
        
        const headers = ['Type', 'Description', 'Reference', 'Date', 'Status', 'Amount (INR)'];
        const csvRows = [headers.join(',')];
        
        transactions.forEach(tx => {
            const date = new Date(tx.createdAt).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            }).replace(/,/g, ' ');
            
            const desc = (tx.description || (tx.type === 'CREDIT' ? 'Booking Earning' : 'Wallet Redemption')).replace(/,/g, '');
            const type = tx.type;
            const ref = tx.razorpayPayoutId || '';
            const status = tx.status;
            const amount = tx.amount;
            
            csvRows.push([type, desc, ref, date, status, amount].join(','));
        });
        
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `transactions_${userType}_${new Date().getTime()}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    useEffect(() => {
        if (partnerId) {
            fetchWalletDetails();

            const interval = setInterval(() => {
                fetchWalletDetails(true);
            }, 15000);

            return () => clearInterval(interval);
        }
    }, [partnerId, userType]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'COMPLETED': return 'text-green-600 bg-green-50';
            case 'PENDING': return 'text-amber-600 bg-amber-50';
            case 'FAILED': return 'text-red-600 bg-red-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'COMPLETED': return <CheckCircle2 size={16} />;
            case 'PENDING': return <Clock size={16} />;
            case 'FAILED': return <XCircle size={16} />;
            default: return null;
        }
    };

    // Styling to match generic Dashboard feel 
    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-8">
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <Wallet className="text-primary" />
                            My Wallet
                        </h1>
                        <p className="text-gray-500 mt-1">Manage your earnings and redemptions</p>
                    </div>
                    <button 
                        onClick={downloadCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium shadow-sm transition-all"
                    >
                        <Download size={18} />
                        Download History
                    </button>
                </div>

                {/* Balance Card */}
                <div className="bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-8 text-white shadow-xl shadow-primary/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                        <Wallet size={120} />
                    </div>
                    <div className="relative z-10">
                        <p className="text-white/80 font-medium mb-2">Available Balance</p>
                        <div className="flex items-baseline gap-2">
                            <span className="text-4xl font-bold">₹</span>
                            <span className="text-5xl font-black tracking-tight">
                                {isLoading ? '...' : (walletData?.balance || 0).toLocaleString('en-IN')}
                            </span>
                        </div>
                        <div className="mt-8 flex gap-4">
                            <button 
                                onClick={() => setIsRedeemModalOpen(true)}
                                disabled={!walletData || walletData.balance < 500}
                                className="bg-white text-primary px-8 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
                            >
                                Redeem Funds
                            </button>
                        </div>
                        {walletData && walletData.balance < 500 && (
                            <p className="text-sm mt-3 text-white/70">
                                Minimum redemption amount is ₹500
                            </p>
                        )}
                    </div>
                </div>

                {/* Transactions Section */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                        <h2 className="text-lg font-bold text-gray-800">Transaction History</h2>
                        <button 
                            onClick={downloadCSV}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold transition-colors border border-gray-200"
                        >
                            <Download size={16} />
                            Export CSV
                        </button>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50 text-gray-500 text-sm">
                                    <th className="p-4 font-medium">Type</th>
                                    <th className="p-4 font-medium">Description</th>
                                    <th className="p-4 font-medium">Date</th>
                                    <th className="p-4 font-medium">Status</th>
                                    <th className="p-4 font-medium text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center text-gray-500">
                                            Loading transactions...
                                        </td>
                                    </tr>
                                ) : transactions.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center text-gray-500">
                                            No transactions found
                                        </td>
                                    </tr>
                                ) : (
                                    transactions.map((tx) => (
                                        <tr key={tx._id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                                            <td className="p-4">
                                                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${tx.type === 'CREDIT' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                                    {tx.type === 'CREDIT' ? <ArrowDownRight size={20} /> : <ArrowUpRight size={20} />}
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <p className="font-medium text-gray-800">{tx.description || (tx.type === 'CREDIT' ? 'Booking Earning' : 'Wallet Redemption')}</p>
                                                {tx.razorpayPayoutId && (
                                                    <p className="text-xs text-gray-500 mt-1">Ref: {tx.razorpayPayoutId}</p>
                                                )}
                                            </td>
                                            <td className="p-4 text-gray-600 text-sm">
                                                {new Date(tx.createdAt).toLocaleDateString('en-IN', {
                                                    day: 'numeric', month: 'short', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit'
                                                })}
                                            </td>
                                            <td className="p-4">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(tx.status)}`}>
                                                    {getStatusIcon(tx.status)}
                                                    {tx.status}
                                                </span>
                                            </td>
                                            <td className={`p-4 text-right font-bold ${tx.type === 'CREDIT' ? 'text-green-600' : 'text-gray-900'}`}>
                                                {tx.type === 'CREDIT' ? '+' : '-'}₹{tx.amount.toLocaleString('en-IN')}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <RedemptionModal 
                isOpen={isRedeemModalOpen}
                onClose={() => setIsRedeemModalOpen(false)}
                maxAmount={walletData?.balance || 0}
                userId={partnerId}
                userType={userType}
                onSuccess={fetchWalletDetails}
            />
        </div>
    );
};

export default WalletPage;
